import React from 'react';

import { Money, formatMoney } from '@betterment/js-runtime';
import { Box, Button, Dialog, Heading, Separator, Text } from '@betterment/samba-react';
import { SambaAutoLink, useFeatureFlag } from '@betterment/shared-components';

import { urlBuilder } from '../../Shared/urlBuilder';

export type Props = {
  closeHandler: () => void;
  netWorth: NetWorth;
};

type NetWorth = {
  balance: Money;
  externalSubtotal: Subtotal;
  internalSubtotal: Subtotal;
};

type Subtotal = {
  balance: Money;
  name: string;
  items: Array<NetWorthItem>;
};

type NetWorthItem = {
  balance: Money;
  title: string;
};

export const NetWorthModal: React.FC<Props> = ({ closeHandler, netWorth }) => {
  const { enabled: isJointTrustFlagEnabled, loading: isJointTrustFlagLoading } = useFeatureFlag({
    splitName: 'retail.joint_trust_ext_aggregation_q4_2024_enabled',
    context: 'NetWorthModal',
  });

  const netWorthDescription = isJointTrustFlagEnabled ? (
    <Text as="p" css={{ py: 'space.150' }}>
      Your total net worth includes all your Betterment accounts and any connected accounts. Accounts assigned to joint
      or trust goals with multiple owners will factor into every owner's net worth.
      <br />
      <br />
      You can choose which accounts to include in your total net worth. To hide connected accounts or view more details,
      visit your connected account settings.
    </Text>
  ) : (
    <Text as="p" css={{ py: 'space.150' }}>
      Your total net worth includes all of your Betterment accounts as well as connected accounts. You can always change
      the accounts included in your total net worth. Accounts that we are unable to categorize are listed under “other.”
      You can hide connected accounts from your net worth calculation and see additional details by visiting your
      connected account settings.
    </Text>
  );

  return (
    <Dialog overlay="default" open={true} onOpenChange={closeHandler}>
      <Dialog.Content
        css={{
          display: 'flex',
          flexDirection: 'column',
          p: 'space.400',
          overflow: 'auto',
        }}
        size="large"
      >
        <Dialog.Title asChild={true}>
          <Heading as="h1" variant="title300">
            Total net worth
          </Heading>
        </Dialog.Title>
        <Box css={{ pb: 'space.400' }}>
          <Dialog.Description asChild={true}>{!isJointTrustFlagLoading && netWorthDescription}</Dialog.Description>
          <SambaAutoLink to={urlBuilder.appExternalAccounts.absolute()}>Manage connected accounts</SambaAutoLink>
        </Box>
        <Box>
          <Box
            css={{
              display: 'flex',
              justifyContent: 'space-between',
              alignSelf: 'stretch',
              pb: 'space.25',
            }}
          >
            <Heading
              as="h5"
              variant="title100"
              css={{
                fontWeight: 600,
              }}
            >
              {netWorth.internalSubtotal.name} accounts
            </Heading>
            <Heading
              as="h5"
              variant="title100"
              css={{
                fontWeight: 600,
                fontVariantNumeric: 'lining-nums tabular-nums',
              }}
            >
              {formatMoney(netWorth.internalSubtotal.balance)}
            </Heading>
          </Box>

          {netWorth.internalSubtotal.items.map(row => (
            <Box
              key={`internal-${row.title}-${row.balance.cents}`}
              css={{
                display: 'flex',
                justifyContent: 'space-between',
                alignSelf: 'stretch',
                gap: 'space.50',
                py: 'space.25',
              }}
            >
              <Text>{row.title}</Text>
              <Text
                variant="text100"
                css={{
                  fontVariantNumeric: 'lining-nums tabular-nums',
                }}
              >
                {formatMoney(row.balance)}
              </Text>
            </Box>
          ))}

          <Box css={{ py: 'space.200' }}>
            <Box
              css={{
                display: 'flex',
                justifyContent: 'space-between',
                alignSelf: 'stretch',
                gap: 'space.50',
                pb: 'space.25',
              }}
            >
              <Heading
                as="h5"
                variant="title100"
                css={{
                  fontWeight: 600,
                }}
              >
                {netWorth.externalSubtotal.name} accounts
              </Heading>
              <Heading
                as="h5"
                variant="title100"
                css={{
                  fontWeight: 600,
                  fontVariantNumeric: 'lining-nums tabular-nums',
                }}
              >
                {formatMoney(netWorth.externalSubtotal.balance)}
              </Heading>
            </Box>

            {netWorth.externalSubtotal.items.map(row => (
              <Box
                key={`external-${row.title}-${row.balance.cents}`}
                css={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignSelf: 'stretch',
                  gap: 'space.50',
                  py: 'space.25',
                }}
              >
                <Text>{row.title}</Text>
                <Text
                  variant="text100"
                  css={{
                    fontVariantNumeric: 'lining-nums tabular-nums',
                  }}
                >
                  {formatMoney(row.balance)}
                </Text>
              </Box>
            ))}
          </Box>

          <Separator />

          <Box css={{ pt: 'space.200' }}>
            <Box
              css={{
                display: 'flex',
                justifyContent: 'space-between',
                alignSelf: 'stretch',
              }}
            >
              <Heading as="h5" variant="title100">
                Total net worth
              </Heading>
              <Heading
                as="h5"
                variant="title100"
                css={{
                  fontWeight: 600,
                  fontVariantNumeric: 'lining-nums tabular-nums',
                }}
              >
                {formatMoney(netWorth.balance)}
              </Heading>
            </Box>
            {!isJointTrustFlagEnabled && !isJointTrustFlagLoading && (
              <Box css={{ pt: 'space.400' }}>
                <Button variant="solidPrimary" onClick={closeHandler}>
                  Close
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </Dialog.Content>
    </Dialog>
  );
};
